import { normalizeForMatch } from './routing.ts';

/**
 * Retail rules of thumb used to find sales opportunities. They live here (one place, tested) and are compiled into
 * SQL where lists are built in the database, so the screens and the queries never disagree.
 */

// ---------------------------------------------------------------------------------------------------------------
// Customer classes (recency and frequency)
// ---------------------------------------------------------------------------------------------------------------

export const CUSTOMER_CLASSES = ['vip', 'loyal', 'new', 'one_time', 'at_risk', 'lapsed', 'prospect'] as const;
export type CustomerClass = (typeof CUSTOMER_CLASSES)[number];

/** Day thresholds behind the classes (fragrance is bought every few months; a year without a purchase is lapsed). */
export const CLASS_RULES = {
  newWithinDays: 30,
  vipOrders: 3,
  vipWithinDays: 90,
  loyalWithinDays: 120,
  lapsedAfterDays: 240,
} as const;

export interface CustomerStats {
  /** Completed orders in the last 365 days (cancelled and returned excluded). */
  ordersYear: number;
  /** Orders ever. */
  orders: number;
  /** Days since the last order; null when they never bought. */
  daysSinceLast: number | null;
}

export function customerClass(s: CustomerStats): CustomerClass {
  const r = CLASS_RULES;
  if (!s.orders || s.daysSinceLast === null) return 'prospect';
  const d = s.daysSinceLast;
  if (d > r.lapsedAfterDays) return 'lapsed';
  if (s.ordersYear >= r.vipOrders && d <= r.vipWithinDays) return 'vip';
  if (s.orders === 1) return d <= r.newWithinDays ? 'new' : 'one_time';
  if (d <= r.loyalWithinDays) return 'loyal';
  return 'at_risk';
}

/** The same rule as SQL, over columns `orders`, `orders_year` and `days_since_last` (int, NULL = never). */
export function customerClassSql(orders: string, ordersYear: string, daysSinceLast: string): string {
  const r = CLASS_RULES;
  return `CASE
    WHEN coalesce(${orders}, 0) = 0 OR ${daysSinceLast} IS NULL THEN 'prospect'
    WHEN ${daysSinceLast} > ${r.lapsedAfterDays} THEN 'lapsed'
    WHEN ${ordersYear} >= ${r.vipOrders} AND ${daysSinceLast} <= ${r.vipWithinDays} THEN 'vip'
    WHEN ${orders} = 1 AND ${daysSinceLast} <= ${r.newWithinDays} THEN 'new'
    WHEN ${orders} = 1 THEN 'one_time'
    WHEN ${daysSinceLast} <= ${r.loyalWithinDays} THEN 'loyal'
    ELSE 'at_risk' END`;
}

// ---------------------------------------------------------------------------------------------------------------
// Replenishment: when a bottle is likely to run out
// ---------------------------------------------------------------------------------------------------------------

/**
 * Days a product typically lasts with regular use. About 0.4 ml a day for perfume (four sprays) means a 100 ml
 * bottle lasts roughly 250 days; bakhoor and body care are used up in about two months. Gift sets are usually
 * bought for someone else, so they do not predict a repurchase.
 */
export const LASTS = {
  mlPerDay: 0.4,
  defaultMl: 100,
  bakhoorDays: 60,
  bodyCareDays: 60,
  oudOilMlPerDay: 0.1,
} as const;

export function lastsDays(p: { category: string; sizeMl?: number | null }): number | null {
  switch (p.category) {
    case 'fragrance':
      return Math.round((p.sizeMl ?? LASTS.defaultMl) / LASTS.mlPerDay);
    case 'oud_oil':
      return Math.round((p.sizeMl ?? 12) / LASTS.oudOilMlPerDay);
    case 'bakhoor':
      return LASTS.bakhoorDays;
    case 'body_care':
      return LASTS.bodyCareDays;
    default:
      return null;
  }
}

/** The same rule as SQL, over a category column and a size column. */
export function lastsDaysSql(category: string, sizeMl: string): string {
  return `CASE ${category}
    WHEN 'fragrance' THEN round(coalesce(${sizeMl}, ${LASTS.defaultMl}) / ${LASTS.mlPerDay})::int
    WHEN 'oud_oil' THEN round(coalesce(${sizeMl}, 12) / ${LASTS.oudOilMlPerDay})::int
    WHEN 'bakhoor' THEN ${LASTS.bakhoorDays}
    WHEN 'body_care' THEN ${LASTS.bodyCareDays}
    ELSE NULL END`;
}

/** A reminder is useful from three weeks before the bottle should run out until six weeks after. */
export const REPLENISH_WINDOW = { beforeDays: 21, afterDays: 45 } as const;

// ---------------------------------------------------------------------------------------------------------------
// Purchase intent in a customer's message
// ---------------------------------------------------------------------------------------------------------------

/**
 * Words that show someone is close to buying (price, stock, ordering, delivery), in English and Gulf Arabic. Used to
 * find enquiries that did not turn into an order. Matching is on normalised text, so Arabic spelling variants match.
 */
const INTENT_WORDS = [
  'price',
  'how much',
  'cost',
  'available',
  'in stock',
  'stock',
  'order',
  'buy',
  'deliver',
  'delivery',
  'offer',
  'discount',
  'gift set',
  'do you have',
  'tester',
  'سعر',
  'السعر',
  'بكم',
  'كم سعر',
  'متوفر',
  'متوفره',
  'موجود',
  'عندكم',
  'اطلب',
  'طلب',
  'ابي',
  'ابغي',
  'ابغى',
  'توصيل',
  'خصم',
  'عرض',
  'هديه',
];
const INTENT_KEYS = INTENT_WORDS.map((w) => normalizeForMatch(w));
const AR_PREFIX = /^(?:[وفبلك])?(?:ال)?/u;

/** One word matches with English endings (orders, delivered) or Arabic prefixes (والسعر, بالتوصيل). */
function wordMatches(token: string, key: string): boolean {
  if (token === key) return true;
  if (/^[a-z]/.test(key)) return token === `${key}s` || token === `${key}ed` || token === `${key}ing`;
  return (
    token.length > key.length &&
    token.endsWith(key) &&
    token.slice(0, -key.length).replace(AR_PREFIX, '') === ''
  );
}

export function hasPurchaseIntent(text: string | null | undefined): boolean {
  if (!text) return false;
  const t = normalizeForMatch(text)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const tokens = t.split(' ');
  return INTENT_KEYS.some((k) =>
    k.includes(' ') ? ` ${t} `.includes(` ${k} `) : tokens.some((w) => wordMatches(w, k)),
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Campaign attribution
// ---------------------------------------------------------------------------------------------------------------

export type AttributionReason = 'offer_code' | 'ad_conversation' | 'campaign_message' | 'automation_message';

export interface AttributionInput {
  placedAt: Date;
  couponCode?: string | null;
  /** Campaigns by their offer code (upper case). */
  campaignsByCode: ReadonlyMap<string, string>;
  /** The campaign whose ad started the conversation this sale was made in. */
  conversationCampaignId?: string | null;
  /** Campaign messages this customer received. */
  touches: readonly { campaignId: string; sentAt: Date; attributionDays: number }[];
  /** Automation messages this customer received (abandoned checkout, refill reminder…). */
  automationTouches?: readonly { automationId: string; sentAt: Date; attributionDays: number }[];
}

export type Attribution =
  | { campaignId: string; automationId?: undefined; reason: Exclude<AttributionReason, 'automation_message'> }
  | { automationId: string; campaignId?: undefined; reason: 'automation_message' };

/**
 * Which campaign gets the credit for an order. In order of certainty: the customer used the campaign's offer
 * code; the sale was made in a conversation that started from the campaign's ad; the customer received a campaign
 * message within its attribution window before buying (the most recent one wins).
 */
export function attributeOrder(i: AttributionInput): Attribution | null {
  const code = i.couponCode?.trim().toUpperCase();
  const byCode = code ? i.campaignsByCode.get(code) : undefined;
  if (byCode) return { campaignId: byCode, reason: 'offer_code' };
  if (i.conversationCampaignId) return { campaignId: i.conversationCampaignId, reason: 'ad_conversation' };
  const at = i.placedAt.getTime();
  const within = (t: { sentAt: Date; attributionDays: number }) =>
    t.sentAt.getTime() <= at && at - t.sentAt.getTime() <= t.attributionDays * 86_400_000;
  // The most recent message wins, whether it came from a campaign or an automation.
  const candidates = [
    ...i.touches.filter(within).map((t) => ({
      at: t.sentAt.getTime(),
      r: { campaignId: t.campaignId, reason: 'campaign_message' } as Attribution,
    })),
    ...(i.automationTouches ?? []).filter(within).map((t) => ({
      at: t.sentAt.getTime(),
      r: { automationId: t.automationId, reason: 'automation_message' } as Attribution,
    })),
  ].sort((a, b) => b.at - a.at);
  return candidates[0]?.r ?? null;
}
